import React, { useEffect, useState } from 'react';
import moment from 'moment';
import Select from 'react-select';
import { SITE_TITLE } from '../../utils/constants';
import { seo } from '../../utils/seo';
import useAuth from '../../hooks/useAuth';
import './AccountMultiplier.css';
import Loader from '../../components/Loader/Loader';
import GearSpinner from '../../components/GearSpinner/GearSpinner';
import printIcon from '../../assets/images/print-img.png';
import savedraftIcon from '../../assets/images/savedraft-icon.png';
import emailIcon from '../../assets/images/email-icon.png';
import pdfIcon from '../../assets/images/pdf-img.png';
import { Validation } from '../../utils/validation';
import AccountMultiplierServices from '../../services/AccountMultiplierService';
import AlertMessage from '../../components/AlertMessage/AlertMessage';

const AccountMultiplierForm = () => {
    const { user } = useAuth();
    const [showSpinner, setShowSpinner] = useState(1);
    const [showLoader, setShowLoader] = useState(0);
    const [isSubmit, setIsSubmit] = useState(0);
    const [commissionScaleData, setCommissionScaleData] = useState([]);
    const [customerTypes, setCustomerTypes] = useState([]);
    const [selectedMultiplierSchedule, setSelectedMultiplierSchedule] = useState([]);
    const [errors, setErrors] = useState({});
    const [alert, setAlert] = useState({ type: '', message: '' });
    const accountMultiplierServices = new AccountMultiplierServices();
    const [formData, setFormData] = useState({
        sales_representative_id: user?.id || "",
        name: "",
        address: "",
        city_state: "",
        postal_code: "",
        purchasing_contact: "",
        telephone_number: "",
        fax_number: "",
        email: "",
        customer_number: "",
        salesman_name: "",
        customer_type: "",          // no radio selected initially
        customer_type_other: "",    // only used if "Other" selected
        customer_requirements: "",
        annual_spend_motors: "",
        annual_techtop_potential: "",
        multiplier_reason: "",
        change_reason: "",
        comments: "",
        effective: "",
        replaces: "",
        approved_by: ""
    });

    useEffect(() => {
        seo({
            title: SITE_TITLE + ' || Account Multiplier Form',
            metaDescription: '',
        });
        getCommissionScale();
    }, [user]);

    const handleSelectChange = (selectedOption, item) => {
        setSelectedMultiplierSchedule(prev => {
            const newSchedule = [...prev];
            const index = newSchedule.findIndex(s => s.multiplier_code === item.multiplier_code);

            const updatedItem = {
                multiplier_code: item.multiplier_code,
                models_involved: item.models_involved,
                commission_scale: selectedOption ? selectedOption.value : '' // <-- handle null
            };

            if (index > -1) {
                newSchedule[index] = updatedItem;
            } else {
                newSchedule.push(updatedItem);
            }

            // Optionally, remove empty selections if you want to remove cleared ones
            return newSchedule.filter(s => s.commission_scale !== '');
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Clear related errors first
        setErrors((prev) => ({
            ...prev,
            api_error: '',
            [name]: '',
            ...(name === 'customer_type_other' ? { customer_type: '' } : {}),
            ...(name === 'customer_type' && value !== 'Other' ? { customer_type_other: '' } : {}),
        }));

        setFormData((prev) => {
            let updated = { ...prev, [name]: value };

            // If user types in "Other" field
            if (name === 'customer_type_other' && value.trim() !== '') {
                updated.customer_type = 'Other';
            }

            // If user changes customer_type away from "Other"
            if (name === 'customer_type' && value !== 'Other') {
                updated.customer_type_other = '';
            }

            return updated;
        });
    };

    const handleSubmit = (e, action) => {
        e.preventDefault();
        setErrors({});
        const dataToValidate = {
            ...formData,
        };
        const errors = Validation(dataToValidate, ['name']);
        if (Object.keys(errors).length > 0) {
            setErrors(errors);
            return;
        }
        submitForm(action);
    };

    const submitForm = async (action) => {
        if(formData.customer_type === 'Other') {
            formData.customer_type = formData.customer_type_other;
        }
        const { customer_type_other, ...fieldsData } = formData;
        let payload = {
            ...fieldsData,
            multiplier_schedule: selectedMultiplierSchedule,
        };
        setIsSubmit(1);
        setAlert({ type: '', message: '' });

        try {
            let response;
            if (action === 'save') {
                response = await accountMultiplierServices.saveAccountMultiplierSchedule(payload);
            } else if (action === 'share') {
                // Assuming you have a separate API for sharing
                response = await accountMultiplierServices.shareAccountMultiplierSchedule(payload);
            } else if (action === 'export_pdf') {
                // fallback or default action - same as save maybe
                payload.export_type = 'pdf';
                const response = await accountMultiplierServices.exportAccountMultiplierSchedule(payload);

                // ✅ handle blob download
                const blob = new Blob([response.data], { type: 'application/pdf' });

                // Use moment to match Django timestamp format (%Y%m%d%H%M%S)
                const timestamp = moment().format('YYYYMMDDHHmmss');
                const companyName = payload.name || 'account';
                const fileName = `${companyName}-account-multiplier-${timestamp}.pdf`;

                const link = document.createElement('a');
                const fileURL = window.URL.createObjectURL(blob);
                link.href = fileURL;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(fileURL);
                setIsSubmit(0);
                setAlert({ type: 'success', message: 'Pdf export successfully' });
                return; // skip alert logic for export
            }

            setIsSubmit(0);
            const data = response?.data;
            if (data.status === 'success') {
                setAlert({ type: 'success', message: data.message || 'Form submitted successfully' });
                if(action === 'save') {
                    // Reset form or any other logic on save
                }
            } else {
                setAlert({ type: 'danger', message: data.message || 'Failed to submit form' });
                setErrors(prev => ({
                    ...prev,
                    'api_error': data.message || 'Failed to submit form'
                }));
            }
        } catch (error) {
            setIsSubmit(0);
            setAlert({ type: 'danger', message: error.message || 'An error occurred' });
            setErrors(prev => ({
                ...prev,
                'api_error': error.message || 'An error occurred'
            }));
        }
        setTimeout(() => { setAlert({ type: '', message: '' }); }, 5000);
    };

    const printReport = async () => {
        const originalTitle = document.title;
        document.title = `Account Multiplier Request - ${formData.name}`;
        window.print();
        document.title = originalTitle;
    }

    const getCommissionScale = () => {
        accountMultiplierServices.getCustomerMultipliers().then(response => {
            const data = response?.data;
            setShowSpinner(0);
            if (data.status === 'success') {
                const schedules = data?.result.multiplier_schedule || [];
                setCommissionScaleData(schedules);
                setCustomerTypes(data?.result.customer_types || []);

                // Build initial selected values as array of objects
                const initialSelected = [];
                schedules.forEach(item => {
                    const selected = item.commission_scale.find(s => s.is_selected);
                    if (selected) {
                        initialSelected.push({
                            multiplier_code: item.multiplier_code,
                            models_involved: item.models_involved,
                            commission_scale: selected.scale
                        });
                    }
                });
                setSelectedMultiplierSchedule(initialSelected);
            }
        });
    };

    const renderMultiplierTable = () => {
        if (showSpinner && commissionScaleData.length === 0) {
            return (
                <tr>
                    <td colSpan={3}>
                        <GearSpinner />
                    </td>
                </tr>
            );
        }

        if (commissionScaleData.length === 0) {
            return (
                <tr>
                    <td colSpan={3}>No Commission Scale Found</td>
                </tr>
            );
        }

        return (
            <React.Fragment>
                {commissionScaleData.map((item, index) => {
                    const options = item.commission_scale.map((scale) => ({
                        value: scale.scale,
                        label: scale.scale,
                    }));

                    // Find currently selected scale from selectedMultiplierSchedule array
                    const currentSelected = selectedMultiplierSchedule.find(
                        s => s.multiplier_code === item.multiplier_code
                    );

                    const defaultSelected = currentSelected
                        ? options.find(opt => opt.value === currentSelected.commission_scale)
                        : null;

                    return (
                        <tr key={index}>
                            <td>{item.models_involved}</td>
                            <td className="text-center">{item.multiplier_code}</td>
                            <td className="text-center">
                                {item.commission_scale.length > 0 && (
                                    <Select
                                        options={options}
                                        value={defaultSelected}
                                        onChange={(selectedOption) =>
                                            handleSelectChange(selectedOption, item)
                                        }
                                        name={`multiplier_schedule[${item.multiplier_code}]`}
                                        isClearable
                                        menuPosition="fixed"
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                    />
                                )}
                            </td>
                        </tr>
                    );
                })}
            </React.Fragment>
        );
    };

    const renderCustomerTypes = () => {
        if (showSpinner && customerTypes.length === 0) {
            return (
                <tbody>
                    <tr>
                        <td colSpan={2}>
                            <GearSpinner />
                        </td>
                    </tr>
                </tbody>
            );
        }
        return (
            <tbody>
                {customerTypes.length > 0 ? (
                    customerTypes.map((type, index) => {
                        const divStyle =
                            type === "Other"
                                ? {
                                    float: "left",
                                    marginRight: "10px",
                                    position: "relative",
                                    top: "5px",
                                }
                                : {};

                        return (
                            <tr key={index}>
                                <td>
                                    <div className="form-check" style={divStyle}>
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            value={type}
                                            id={`customer-type-${index}`}
                                            name="customer_type"
                                            checked={formData.customer_type === type}
                                            onChange={handleInputChange}
                                        />
                                        <label
                                            className="form-check-label"
                                            htmlFor={`customer-type-${index}`}
                                        >
                                            {type}
                                        </label>
                                    </div>

                                    {type === "Other" && (
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="customer-type-other"
                                            name="customer_type_other"
                                            placeholder="Description here"
                                            style={{ width: "74%" }}
                                            value={formData.customer_type_other}
                                            onChange={handleInputChange}
                                        />
                                    )}
                                </td>
                            </tr>
                        );
                    })
                ) : (
                    <tr>
                        <td colSpan={2}>No Customer Types Found</td>
                    </tr>
                )}
            </tbody>
        );
    };

    const renderMultiplierForm = () => {
        return (
            <React.Fragment>
                <div className="row">
                    <div className="col-md-12 mt-3">
                        <table className="table table-multiple-schedule1">
                            <tbody>
                                <tr>
                                    <th><label htmlFor="name">Name:</label></th>
                                    <td>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            placeholder="Enter name"
                                        />
                                        {errors.name && <div className="text-danger">{errors.name}</div>}
                                    </td>
                                </tr>
                                <tr>
                                    <th><label htmlFor="address">Address:</label></th>
                                    <td>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="address"
                                            name="address"
                                            value={formData.address}
                                            onChange={handleInputChange}
                                            placeholder="Enter address"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th><label htmlFor="city_state">City, State</label></th>
                                    <td>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="city_state"
                                            name="city_state"
                                            value={formData.city_state}
                                            onChange={handleInputChange}
                                            placeholder="Enter city, state"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th><label htmlFor="postal_code">Postal Code</label></th>
                                    <td>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="postal_code"
                                            name="postal_code"
                                            value={formData.postal_code}
                                            onChange={handleInputChange}
                                            placeholder="Enter postal code"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th><label htmlFor="purchasing_contact">Purchasing Contact</label></th>
                                    <td>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="purchasing_contact"
                                            name="purchasing_contact"
                                            value={formData.purchasing_contact}
                                            onChange={handleInputChange}
                                            placeholder="Enter purchasing contact"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th><label htmlFor="telephone_number">Tel: No.</label></th>
                                    <td>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="telephone_number"
                                            name="telephone_number"
                                            value={formData.telephone_number}
                                            onChange={handleInputChange}
                                            placeholder="Enter telephone number"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th><label htmlFor="fax_number">Fax No.</label></th>
                                    <td>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="fax_number"
                                            name="fax_number"
                                            value={formData.fax_number}
                                            onChange={handleInputChange}
                                            placeholder="Enter fax number"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th><label htmlFor="email">E-mail</label></th>
                                    <td>
                                        <input
                                            type="email"
                                            className="form-control"
                                            id="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            placeholder="Enter email"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th><label htmlFor="customer_number">Customer No.</label></th>
                                    <td>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="customer_number"
                                            name="customer_number"
                                            value={formData.customer_number}
                                            onChange={handleInputChange}
                                            placeholder="Enter customer number"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th><label htmlFor="salesman_name">Salesman Name</label></th>
                                    <td>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="salesman_name"
                                            name="salesman_name"
                                            value={formData.salesman_name}
                                            onChange={handleInputChange}
                                            placeholder="Enter salesman name"
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-8">
                        <div className="table-responsive">
                            <table className="table table-bordered table-multiple-schedule2" id="techtop-net-pricing-report-table">
                                <thead>
                                    <tr>
                                        <th colSpan="3" className="text-center">MULTIPLIER SCHEDULE</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td className="text-center">Prod. Desc. Motors</td>
                                        <td className="text-center">Mult.Ref</td>
                                        <td className="text-center">Multiplier</td>
                                    </tr>
                                    {renderMultiplierTable()}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="table-responsive">
                            <table className="table table-bordered table-multiple-schedule3" id="techtop-net-pricing-report-table">
                                <thead>
                                    <tr>
                                        <th colSpan="3" className="text-center">TYPE OF CUSTOMER (CHECK ONE)</th>
                                    </tr>
                                </thead>
                                {renderCustomerTypes()}
                            </table>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-12">
                        <h6 className="dark-color1">Customer Requirements:</h6>
                        <textarea
                            className="form-control"
                            rows="3"
                            placeholder="Customer Requirements Here..."
                            onChange={handleInputChange}
                            name="customer_requirements"
                            value={formData.customer_requirements}
                        ></textarea>
                    </div>
                    <div className="col-md-12 mt-3">
                        <h6 className="dark-color1">JUSTIFICATION FOR MULTIPLIER (TO BE COMPLETED BY ORIGINATING PARTY)</h6>
                        <table className="table table-multiple-schedule4">
                            <tbody>
                                <tr>
                                    <th><label htmlFor="annual_spend_motors">1) Annual spend on motors </label></th>
                                    <td><input type="text" className="form-control" id="annual_spend_motors" placeholder="" onChange={handleInputChange} name="annual_spend_motors" value={formData.annual_spend_motors} /></td>
                                </tr>
                                <tr>
                                    <th><label htmlFor="annual-spend_input2">2) Annual Techtop potential </label></th>
                                    <td><input type="text" className="form-control" id="annual_techtop_potential" placeholder="" onChange={handleInputChange} name="annual_techtop_potential" value={formData.annual_techtop_potential} /></td>
                                </tr>
                                <tr>
                                    <th><label htmlFor="multiplier_reason">3) Reason for multiplier </label></th>
                                    <td><input type="text" className="form-control" id="multiplier_reason" placeholder="" onChange={handleInputChange} name="multiplier_reason" value={formData.multiplier_reason} /></td>
                                </tr>
                                <tr>
                                    <th><label htmlFor="change_reason">4) Reason for change to Techtop </label></th>
                                    <td><input type="text" className="form-control" id="change_reason" placeholder="" onChange={handleInputChange} name="change_reason" value={formData.change_reason} /></td>
                                </tr>
                            </tbody>
                        </table>
                        <h6 className="dark-color1">Comments:</h6>
                        <textarea
                            className="form-control"
                            rows="3"
                            placeholder="Comments..."
                            onChange={handleInputChange}
                            name="comments"
                            value={formData.comments}
                        ></textarea>
                        <table className="table table-multiple-schedule5 mt-3">
                            <tbody>
                                <tr>
                                    <td rowSpan="3">This area: To be completed by Techtop personnel</td>
                                    <th><label htmlFor="effective">Effective</label></th>
                                    <td><input type="text" className="form-control" id="effective" name="effective" onChange={handleInputChange} value={formData.effective} placeholder="" /></td>
                                </tr>
                                <tr>
                                    <th><label htmlFor="replaces">Replaces</label></th>
                                    <td><input type="text" className="form-control" id="replaces" name="replaces" onChange={handleInputChange} value={formData.replaces} placeholder="" /></td>
                                </tr>
                                <tr>
                                    <th><label htmlFor="approved_by">Approved by</label></th>
                                    <td><input type="text" className="form-control" id="approved_by" name="approved_by" onChange={handleInputChange} value={formData.approved_by} placeholder="" /></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

            </React.Fragment>
        );
    };

    return (
        <React.Fragment>
            <main className="techtop-middle-div">
                <section className="techtop-multipler-schedule-form-section pb-5">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="techtop-default-title2">
                                    <div className="row">
                                        <div className="col-6">
                                            <h1 className="m-0 dark-color1">Account Multiplier Form</h1>
                                        </div>
                                        <div className="col-6">
                                            <h2 className="m-0 dark-color1 text-end">{user?.name}</h2>
                                        </div>
                                    </div>
                                </div>
                                <AlertMessage
                                    type={alert.type}
                                    message={alert.message}
                                    onClose={() => setAlert({ type: '', message: '' })}
                                />
                                <form action="#" method="post" id="account-multiplier-form">
                                    <div className="row">
                                        <div className="col-md-9">
                                            <div className="row">
                                                <div className="col-md-12 mt-3">
                                                    <h4 className="dark-color1 text-center">New Account</h4>
                                                    <h3 className="dark-color1 text-center">Multiplier Form</h3>
                                                    {renderMultiplierForm()}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3 mt-3">
                                            <ul className="net-pricing-report-list">
                                                <li><a href="#" onClick={(e) => { e.preventDefault(); handleSubmit(e, 'save'); }}><img src={savedraftIcon} alt="Save Draft" /> Save Draft</a></li>
                                                <li><a href="#" onClick={(e) => {e.preventDefault(); printReport()}}><img src={printIcon} alt="Print" /> Print</a></li>
                                                <li><a href="#" onClick={(e) => { e.preventDefault(); handleSubmit(e, 'share'); }}><img src={emailIcon} alt="Email" /> Email</a></li>
                                                <li><a href="#" onClick={(e) => { e.preventDefault(); handleSubmit(e, 'export_pdf'); }}><img src={pdfIcon} alt="Export Pdf" />Export PDF</a></li>
                                            </ul>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                    <Loader isLoading={isSubmit} message="Processing..." />
                </section>

            </main>
        </React.Fragment>
    );
}

export default AccountMultiplierForm;